import { strings, activeLocale, dateLocale } from "../resources/strings";
import { formatString } from "./UtilityFunctions";

/** Whole days from today to the target (negative = past), ignoring time of day. */
const daysUntil = (timestampSeconds: number): number => {
  const targetDate = new Date(timestampSeconds * 1000);
  const currentDate = new Date();

  // Set both dates to start of day to get accurate day difference
  targetDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);

  const timeDifference = targetDate.getTime() - currentDate.getTime();
  return Math.ceil(timeDifference / (1000 * 3600 * 24));
};

export const calculateDaysUntil = (timestampSeconds: number): string => {
  const daysDifference = daysUntil(timestampSeconds);

  if (daysDifference === 0) {
    return strings.countdown.today;
  } else if (daysDifference === 1) {
    return strings.countdown.oneDay;
  } else if (daysDifference > 1) {
    return formatString(strings.countdown.days, String(daysDifference));
  } else {
    // Past date
    const pastDays = Math.abs(daysDifference);
    return pastDays === 1
      ? strings.countdown.oneDayAgo
      : formatString(strings.countdown.daysAgo, String(pastDays));
  }
};

export const formatCountdownText = (timestampSeconds: number): string => {
  // Branch on the day count, not the rendered phrase: composing "Takes place
  // in" with a past phrase produced "Takes place in 1 day ago" (and worse in
  // Spanish), so past dates get their own sentence template.
  const days = daysUntil(timestampSeconds);
  if (days === 0) {
    return strings.countdown.takesPlaceToday;
  }
  const phrase = calculateDaysUntil(timestampSeconds);
  return days > 0
    ? formatString(strings.countdown.takesPlaceIn, phrase)
    : formatString(strings.countdown.takesPlacePast, phrase);
};

export const formatDateTime = (
  seconds: number,
  endSeconds?: number,
): string => {
  const date = new Date(seconds * 1000);
  // activeLocale for the WORDS (weekday/month) so they match the UI language;
  // dateLocale for the time so regional conventions (12h vs 24h) are kept.
  // Composition comes from a per-language template — word order and
  // separators differ between English and Spanish.
  const dayOfWeek = date.toLocaleDateString(activeLocale, { weekday: "short" });
  const month = date.toLocaleDateString(activeLocale, { month: "short" });
  const day = String(date.getDate());
  const time = date.toLocaleTimeString(dateLocale, { timeStyle: "short" });

  if (endSeconds) {
    const endTime = new Date(endSeconds * 1000).toLocaleTimeString(dateLocale, {
      timeStyle: "short",
    });
    return formatString(
      strings.dateTimeAtRange,
      dayOfWeek,
      month,
      day,
      time,
      endTime,
    );
  }

  return formatString(strings.dateTimeAt, dayOfWeek, month, day, time);
};
